import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Text from 'components/_UI/Text';
import Button from 'components/_UI/Button';
import Icon from 'components/_UI/Icon';
import Loader from 'components/_UI/Loader';
import { Row } from 'styled/alignment/Row';
import { Column } from 'styled/alignment/Column';
import { FullyCentered } from 'styled/alignment/Center';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import { useEstablishmentFilter } from 'context/establishments/EstablishmentFilter';
import { useUserLocation } from 'context/user/UserLocation';
import establishmentsApi from 'api/requests/establishments';
import { Calendar, MapPin, X } from 'lucide-react';
import { getCategoryIcon } from 'modules/events/categories/categoryIconMap';
import { transformedDateFilters } from 'theme/filters';

const MIN_RANGE = 1;
const MAX_RANGE = 50;
const DEFAULT_RANGE = 25;

export default function EstablishmentFiltersModal({ isOpen, onClose }) {
    const { establishmentFilter, setEstablishmentFilter } = useEstablishmentFilter();
    const { userLocation } = useUserLocation();

    const [distance, setDistance] = useState(establishmentFilter?.distance_range || DEFAULT_RANGE);
    const [date, setDate] = useState(establishmentFilter?.dates || null);
    const [selectedTags, setSelectedTags] = useState(establishmentFilter?.tags || []);
    const [selectedFeatures, setSelectedFeatures] = useState(establishmentFilter?.features || []);
    const [tags, setTags] = useState([]);
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchOptions();
            setDistance(establishmentFilter?.distance_range || DEFAULT_RANGE);
            setDate(establishmentFilter?.dates || null);
            setSelectedTags(establishmentFilter?.tags || []);
            setSelectedFeatures(establishmentFilter?.features || []);
        }
    }, [isOpen]);

    async function fetchOptions() {
        setLoading(true);
        try {
            const { data } = await establishmentsApi.getOptions();
            setTags(data?.tags || []);
            setFeatures(data?.features || []);
        } catch (error) {
            console.error('Error fetching establishment options:', error);
        } finally {
            setLoading(false);
        }
    }

    function toggleTag(id) {
        setSelectedTags(prev =>
            prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
        );
    }

    function toggleFeature(id) {
        setSelectedFeatures(prev =>
            prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
        );
    }

    function handleClear() {
        setDistance(DEFAULT_RANGE);
        setDate(null);
        setSelectedTags([]);
        setSelectedFeatures([]);
        setEstablishmentFilter(null);
        onClose();
    }

    const hasLocalFilters = !!date || selectedTags.length > 0 || selectedFeatures.length > 0 ||
        (userLocation?.lat && distance !== DEFAULT_RANGE);

    function handleApply() {
        setEstablishmentFilter(hasLocalFilters ? {
            distance_range: userLocation?.lat ? distance : null,
            lat: userLocation?.lat || null,
            long: userLocation?.long || null,
            dates: date,
            tags: selectedTags,
            features: selectedFeatures,
        } : null);
        onClose();
    }

    if (!isOpen) return null;

    return (
        <Overlay onClick={onClose}>
            <ModalCard onClick={(e) => e.stopPropagation()}>
                <CloseButton onClick={onClose}>
                    <X size={18} color={colors.nightDark} />
                </CloseButton>

                <ScrollArea>
                    {(userLocation?.lat && userLocation?.long) && (
                        <Section>
                            <SectionHeader>
                                <Row verticalCenter width='auto'>
                                    <MapPin size={18} color={colors.secondaryPurple} />
                                    <Text
                                        name='highlight'
                                        type='bold'
                                        color={colors.secondaryPurple}
                                        paddingBottom={0}
                                        marginLeft='8px'
                                    >
                                        Distância
                                    </Text>
                                </Row>
                                <Text paddingBottom={0} color={colors.secondaryPurple} type='bold'>
                                    {distance}km
                                </Text>
                            </SectionHeader>
                            <Text name='small' color={colors.nightDark}>
                                Escolha uma distância personalizada
                            </Text>
                            <StyledRange
                                type='range'
                                min={MIN_RANGE}
                                max={MAX_RANGE}
                                value={distance}
                                onChange={(e) => setDistance(Number(e.target.value))}
                            />
                        </Section>
                    )}

                    <Section>
                        <Row verticalCenter width='auto' marginBottom='4px'>
                            <Calendar size={18} color={colors.secondaryPurple} />
                            <Text
                                name='highlight'
                                type='bold'
                                color={colors.secondaryPurple}
                                paddingBottom={0}
                                marginLeft='8px'
                            >
                                Data
                            </Text>
                        </Row>
                        <Text name='small' color={colors.nightDark}>
                            Escolha o período que deseja buscar.
                        </Text>
                        <StyledSelect
                            value={date || ''}
                            onChange={(e) => setDate(e.target.value || null)}
                        >
                            <option value=''>Selecione um período</option>
                            {transformedDateFilters.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </StyledSelect>
                    </Section>

                    {loading ? (
                        <FullyCentered height='100px'>
                            <Loader />
                        </FullyCentered>
                    ) : (
                        <>
                            {features.length > 0 && (
                                <Section>
                                    <Text
                                        name='highlight'
                                        type='bold'
                                        color={colors.secondaryPurple}
                                        paddingBottom={0}
                                        marginBottom='4px'
                                    >
                                        Características do local
                                    </Text>
                                    <Text name='small' color={colors.nightDark}>
                                        Escolha as características que você busca no local.
                                    </Text>
                                    <FeaturesGrid>
                                        {features.map(feature => (
                                            <FeatureItem
                                                key={feature.id}
                                                $selected={selectedFeatures.includes(feature.id)}
                                                onClick={() => toggleFeature(feature.id)}
                                            >
                                                <Column width='22px' height='22px'>
                                                    <Icon
                                                        name={feature.icon}
                                                        color={selectedFeatures.includes(feature.id)
                                                            ? colors.neutral
                                                            : colors.night
                                                        }
                                                    />
                                                </Column>
                                                <Text
                                                    name='mini'
                                                    marginTop='4px'
                                                    textAlign='center'
                                                    type='bold'
                                                    color={selectedFeatures.includes(feature.id)
                                                        ? colors.neutral
                                                        : colors.night
                                                    }
                                                >
                                                    {feature.name}
                                                </Text>
                                            </FeatureItem>
                                        ))}
                                    </FeaturesGrid>
                                </Section>
                            )}

                            {tags.length > 0 && (
                                <Section>
                                    <Text
                                        name='highlight'
                                        type='bold'
                                        color={colors.secondaryPurple}
                                        paddingBottom={0}
                                        marginBottom='4px'
                                    >
                                        Categoria
                                    </Text>
                                    <Text name='small' color={colors.nightDark}>
                                        Escolha as categorias que deseja buscar.
                                    </Text>
                                    <CategoriesGrid>
                                        {tags.map(tag => {
                                            const isSelected = selectedTags.includes(tag.id);
                                            const TagIcon = getCategoryIcon(tag.icon);
                                            return (
                                                <CategoryChip
                                                    key={tag.id}
                                                    $selected={isSelected}
                                                    onClick={() => toggleTag(tag.id)}
                                                >
                                                    <TagIcon size={14} color={isSelected ? colors.neutralLight : colors.secondaryPurple} />
                                                    <Text
                                                        name='small'
                                                        paddingBottom={0}
                                                        color={isSelected ? colors.neutralLight : colors.secondaryPurple}
                                                    >
                                                        {tag.name}
                                                    </Text>
                                                </CategoryChip>
                                            );
                                        })}
                                    </CategoriesGrid>
                                </Section>
                            )}
                        </>
                    )}
                </ScrollArea>

                <ModalFooter>
                    <Column width='48%'>
                        <Button
                            category='roundedBorded'
                            variant={!hasLocalFilters ? 'disabled' : undefined}
                            marginBottom={0}
                            onClick={handleClear}
                        >
                            Limpar
                        </Button>
                    </Column>
                    <Column width='48%'>
                        <Button variant='featured' marginBottom={0} onClick={handleApply}>
                            Aplicar Filtros
                        </Button>
                    </Column>
                </ModalFooter>
            </ModalCard>
        </Overlay>
    );
}

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(26, 26, 26, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 16px;
`;

const ModalCard = styled.div`
    position: relative;
    background: ${colors.neutralLight};
    border-radius: 12px;
    width: 100%;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    @media screen and (min-width: ${breakpoints.lg}) {
        width: 700px;
        max-height: 85vh;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 12px;
    right: 12px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.nightLight};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 1;
    transition: background 0.2s ease;

    &:hover { background: ${colors.neutralDark}; }
`;

const ScrollArea = styled(Column)`
    overflow-y: auto;
    flex: 1;
    padding: 40px 28px 16px 28px;
    align-items: flex-start;
`;

const Section = styled(Column)`
    width: 100%;
    align-items: flex-start;
    margin-bottom: 24px;
`;

const SectionHeader = styled(Row)`
    width: 100%;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
`;

const StyledRange = styled.input`
    width: 100%;
    margin-top: 12px;
    accent-color: ${colors.secondaryPurple};
    height: 6px;
    cursor: pointer;
`;

const CategoriesGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 12px;
    width: 100%;
`;

const CategoryChip = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 16px;
    border-radius: 8px;
    border: 1px solid ${colors.secondaryPurple};
    background: ${({ $selected }) => $selected ? colors.secondaryPurple : 'transparent'};
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: ${({ $selected }) => $selected ? colors.secondaryPurple : `${colors.secondaryPurple}18`};
    }
`;

const FeaturesGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;
    width: 100%;
    margin-top: 12px;
`;

const FeatureItem = styled.div`
    width: 30%;
    margin-bottom: 3%;
    border: 1px solid ${colors.night};
    padding: 8px 5px;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    & > p { padding-bottom: 0; }

    @media screen and (min-width: ${breakpoints.lg}) {
        width: 15%;
    }

    ${({ $selected }) => $selected && `
        background: #4617ad;
        border: 1px solid #4617ad;
    `}
`;

const StyledSelect = styled.select`
    width: 100%;
    margin-top: 10px;
    padding: 14px 16px;
    border: 1px solid ${colors.nightLight};
    border-radius: 8px;
    appearance: none;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E") no-repeat right 14px center;
    font-size: 14px;
    color: ${colors.nightDark};
    cursor: pointer;

    &:focus {
        outline: none;
        border-color: ${colors.secondaryPurple};
    }
`;

const ModalFooter = styled(Row)`
    padding: 16px 28px;
    border-top: 1px solid ${colors.nightLight};
    justify-content: space-between;
    background: ${colors.neutralLight};
    flex-shrink: 0;
`;
